import { useHandleContactDetailsEnrichment } from '@aibees/elephant/components/quick-info-group/hooks/use-handle-enrich-contact-details';
import { usePersonReadDataContext } from '@aibees/elephant/context';
import { useSearchTypeFromRoute, useTrackEvents } from '@aibees/elephant/hooks';
import { useCallback, useMemo, useState } from 'react';
import { createConfig, createTabConfig, tabsUpdatesMapper } from '../config';
import { useRefs } from '../hooks/use-refs';
import {
  contactDetailsAvailableItems,
  contactDetailsUnlockableItems,
  doesContactDetailHaveAnyUpdates,
  isJobChangeHappened,
} from '../utils';
import {
  ContactDetailsConfigProvider,
  ContactDetailsDataProvider,
  ContactDetailsStateProvider,
} from './contact-details-context';
import {
  ContactDetailsContextProps as ContextProps,
  ParentFeature,
} from './types';

export const ContactDetailsProvider = ({
  children,
  parentFeature,
}: ContextProps) => {
  const { api } = usePersonReadDataContext();
  const contactDetailsTabsData = useMemo(
    () => createTabConfig(api?.data),
    [api?.data]
  );
  const [showSeeMore, setShowSeeMore] = useState(false);
  const [showFullItems, setShowFullItems] = useState(false);
  const refs = useRefs();
  const searchType = useSearchTypeFromRoute();
  const { track } = useTrackEvents();
  const { handleEnrichContactDetails, loading, unlocked, failed, disabled } =
    useHandleContactDetailsEnrichment(api?.data, () => {
      track('UNLOCK_CONTACT_DETAILS', {
        multiple: false,
        retry: failed,
        update: unlocked,
        component: parentFeature,
        searchType,
      });
    });
  const hasUpdates = doesContactDetailHaveAnyUpdates(api?.data);
  const availableItems = contactDetailsAvailableItems(api?.data);
  const unlockableItems = contactDetailsUnlockableItems(api?.data);
  const hasJobChange = isJobChangeHappened(api?.data);

  const sortedItemsWithContentCheck = useMemo(
    () =>
      contactDetailsTabsData.filter((i) =>
        api?.data?.available?.includes(tabsUpdatesMapper[i.key])
      ),
    [contactDetailsTabsData, api?.data?.available]
  );

  const initialActiveTab = useMemo(() => {
    if (sortedItemsWithContentCheck.length > 0) {
      return contactDetailsTabsData.findIndex(
        (item) => item.key === sortedItemsWithContentCheck[0].key
      );
    }
    return 0;
  }, [sortedItemsWithContentCheck, contactDetailsTabsData]);

  const [activeTab, setActiveTab] = useState(initialActiveTab);

  const isPopoverType =
    parentFeature === ParentFeature.QUICK_INFO_CONTACT_DETAILS_POPOVER;

  const config = useMemo(() => createConfig(setShowSeeMore), [setShowSeeMore]);

  const handleTabClick = useCallback(
    (index: number) => {
      if (!config[index].active) return;
      setActiveTab(index);
      setShowFullItems(false);

      const ref = refs[config[index].refName as keyof typeof refs];
      if (ref && ref.current) {
        ref.current.scrollIntoView({ behavior: 'smooth' });
      }
    },
    [config, refs]
  );

  const stateValue = useMemo(
    () => ({ loading, unlocked, failed, disabled, isPopoverType }),
    [loading, unlocked, failed, disabled, isPopoverType]
  );
  const configValue = useMemo(
    () => ({
      activeTab,
      setActiveTab,
      refs,
      handleTabClick,
      config,
      showFullItems,
      setShowFullItems,
      showSeeMore,
      setShowSeeMore,
    }),
    [
      activeTab,
      setActiveTab,
      refs,
      handleTabClick,
      config,
      showFullItems,
      setShowFullItems,
      showSeeMore,
      setShowSeeMore,
    ]
  );
  const dataValue = useMemo(
    () => ({
      handleEnrichContactDetails,
      sortedItemsWithContentCheck,
      hasUpdates,
      availableItems,
      unlockableItems,
      hasJobChange,
    }),
    [
      handleEnrichContactDetails,
      sortedItemsWithContentCheck,
      hasUpdates,
      availableItems,
      unlockableItems,
      hasJobChange,
    ]
  );

  return (
    <ContactDetailsStateProvider value={stateValue}>
      <ContactDetailsConfigProvider value={configValue}>
        <ContactDetailsDataProvider value={dataValue}>
          {children}
        </ContactDetailsDataProvider>
      </ContactDetailsConfigProvider>
    </ContactDetailsStateProvider>
  );
};
